<template>
  <app-layout>
    <div class="sc-container">
      <div class="page-wrap">

        <!-- ===== SIDEBAR ===== -->
        <aside class="sidebar-card">
          <div class="sb-avatar-wrap">
            <img
              v-if="user.avatar"
              :src="$avatarSrc(user.avatar)"
              class="sb-avatar"
              alt=""
            />
            <img v-else src="/assets/avatar.png" class="sb-avatar" alt="" />
          </div>
          <div class="sb-identity">
            <p class="sb-name">{{ user.name }}</p>
            <p class="sb-prof" v-if="user.profession">{{ user.profession }}</p>
          </div>
          <div class="sb-stats">
            <div class="sb-stat">
              <span class="sb-stat-val">{{ total_followers }}</span>
              <span class="sb-stat-lbl">Following</span>
            </div>
            <div class="sb-stat">
              <span class="sb-stat-val">{{ total_project }}</span>
              <span class="sb-stat-lbl">Projects</span>
            </div>
          </div>
          <nav class="sb-nav">
            <router-link v-if="user.slug" :to="'/sc/profile/' + user.slug" class="sb-nav-link">View Profile</router-link>
            <router-link to="/my-blog-history" class="sb-nav-link">My Blogs</router-link>
            <router-link to="/my-service-requests" class="sb-nav-link sb-nav-link--active">Service Requests</router-link>
          </nav>
        </aside>

        <!-- ===== MAIN ===== -->
        <main class="main-col">
          <!-- Header -->
          <div class="page-header">
            <div>
              <h1 class="page-title">My Service Requests</h1>
              <p class="page-sub">Track and manage the services you've requested.</p>
            </div>
            <button type="button" class="btn-primary" @click="showCreateModal = true">
              <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
              New Request
            </button>
          </div>

          <!-- Requests grid -->
          <div v-if="details.length" class="requests-grid">
            <router-link
              v-for="data in details"
              :key="data.id"
              :to="'/service/' + data.slug"
              class="request-card"
            >
              <div class="request-card-inner">
                <div class="request-icon">
                  <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                </div>
                <div class="request-info">
                  <span class="request-service">{{ data.service }}</span>
                  <p class="request-desc" v-html="data.description ? data.description.substring(0, 160) + '...' : ''"></p>
                  <span class="request-date">
                    <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                    Posted {{ formatDate2(data.created_at) }}
                  </span>
                </div>
                <svg class="request-arrow" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
              </div>
            </router-link>
          </div>

          <!-- Empty -->
          <div v-else class="empty-state">
            <svg width="56" height="56" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            <p>No service requests yet.</p>
            <button type="button" class="btn-primary" @click="showCreateModal = true">Make a Request</button>
          </div>

          <!-- Pagination -->
          <div v-if="details.length > 0" class="pagination-bar">
            <button class="pg-btn" :disabled="!pagination.prevPageUrl" @click="fetchData(pagination.prevPageUrl)">
              <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
              Prev
            </button>
            <span class="pg-info">Page {{ pagination.currentPage }} of {{ pagination.lastPage }}</span>
            <button class="pg-btn" :disabled="!pagination.nextPageUrl" @click="fetchData(pagination.nextPageUrl)">
              Next
              <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        </main>
      </div>
    </div>

    <!-- ===== CREATE REQUEST MODAL ===== -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal-panel modal-panel--wide">
        <div class="modal-header">
          <h2 class="modal-title">Request a Service</h2>
          <button type="button" class="modal-close" @click="showCreateModal = false">
            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <form id="message-form">
            <div class="sc-field">
              <label>Service Provider Needed <span class="req">*</span></label>
              <select name="service" v-model="service.service">
                <option value="" disabled>Select Service</option>
                <option v-for="(p, i) in professions" :key="i" :value="p.name">{{ p.name }}</option>
              </select>
            </div>

            <div class="sc-field">
              <label>Job Description <span class="req">*</span></label>
              <QuillEditor id="job_editor" v-model:content="service.description" contentType="html" :toolbar="customToolbar" />
            </div>

            <p class="field-group-label">Project Location <span class="req">*</span></p>
            <div class="form-grid-3">
              <div class="sc-field">
                <label>Country</label>
                <select name="country_id" v-model="service.country_id" @change="fetchStates($event)">
                  <option value="" disabled>Country</option>
                  <option v-for="c in country" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="sc-field">
                <label>State</label>
                <select name="state_id" v-model="service.state_id" @change="fetchCities($event)">
                  <option value="" disabled>State</option>
                  <option v-for="s in state" :key="s.id" :value="s.id">{{ s.name }}</option>
                </select>
              </div>
              <div class="sc-field">
                <label>City</label>
                <select name="city_id" v-model="service.city_id">
                  <option value="" disabled>City</option>
                  <option v-for="c in city" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
            </div>

            <div class="sc-field">
              <label>Address</label>
              <textarea name="address" v-model="service.address" rows="2" placeholder="Street address..."></textarea>
            </div>

            <div class="sc-field">
              <label>Deadline for Job Application</label>
              <input type="date" name="end_date" v-model="service.end_date" />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-ghost" @click="showCreateModal = false">Cancel</button>
          <button type="button" class="btn-primary" :disabled="loading" @click="createRequest()">
            <span v-if="loading" class="sc-spinner"></span>
            <span v-else>Submit Request</span>
          </button>
        </div>
      </div>
    </div>

  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout.vue';
import axios from 'axios';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import moment from 'moment';

export default {
  name: 'ServiceRequests',
  components: { AppLayout, QuillEditor },

  created() {
    this.fetchData();
    this.fetchUser();
    this.fetchProfessions();
    this.fetchTotalProjects();
    this.fetchTotalFollowers();
    this.fetchCountries();
    this.fetchNigeriaStates();
    this.fetchLagosCities();
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        document.title = to.meta.title || 'My Service Requests';
      },
    },
  },

  data() {
    return {
      loading: false,
      details: [],
      user: {},
      total_project: 0,
      total_followers: 0,
      professions: [],
      country: [],
      state: [],
      city: [],
      showCreateModal: false,
      pagination: {
        lastPage: '',
        currentPage: '',
        total: '',
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: '',
      },
      show: false,
      service: {
        service: '',
        description: '',
        country_id: 160,
        state_id: '',
        city_id: '',
        address: '',
        end_date: '',
        user_id: localStorage.getItem('user'),
      },
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
    };
  },

  computed: {
    image_url() {
      return process.env.VUE_APP_IMAGE_URL;
    },
  },

  methods: {
    fetchData(url) {
      const endpoint = url || process.env.VUE_APP_API_URL + 'service/' + localStorage.getItem('user');
      axios(endpoint).then(response => {
        if (response.status !== 200) return;
        this.show = false;
        let data = response.data;
        this.details = data.data;
        this.configPagination(data);
      }).catch(() => {});
    },

    fetchUser() {
      axios(process.env.VUE_APP_API_URL + 'user/details/' + localStorage.getItem('user')).then(response => {
        if (response.status !== 200) return;
        this.user = response.data;
      }).catch(() => {});
    },

    fetchProfessions() {
      axios(process.env.VUE_APP_API_URL + 'professions').then(response => {
        if (response.status !== 200) return;
        this.professions = response.data;
      }).catch(() => {});
    },

    fetchCountries() {
      axios(process.env.VUE_APP_API_URL + 'country').then(response => {
        if (!response.status == 200) return;
        this.country = response.data;
      }).catch(() => {});
    },

    fetchStates(e) {
      axios(process.env.VUE_APP_API_URL + 'state/' + e.target.value).then(response => {
        if (response.status !== 200) return;
        this.state = response.data;
      }).catch(() => {});
    },

    fetchCities(e) {
      axios(process.env.VUE_APP_API_URL + 'city/' + e.target.value).then(response => {
        if (response.status !== 200) return;
        this.city = response.data;
      }).catch(() => {});
    },

    fetchNigeriaStates() {
      axios(process.env.VUE_APP_API_URL + 'state/' + 160).then(response => {
        if (!response.status == 200) return;
        this.state = response.data;
      }).catch(() => {});
    },

    fetchLagosCities() {
      axios(process.env.VUE_APP_API_URL + 'city/' + 2671).then(response => {
        if (!response.status == 200) return;
        this.city = response.data;
      }).catch(() => {});
    },

    fetchTotalProjects() {
      axios(process.env.VUE_APP_API_URL + 'project/total-project/' + localStorage.getItem('user')).then(response => {
        if (response.status !== 200) return;
        this.total_project = response.data;
      }).catch(() => {});
    },

    fetchTotalFollowers() {
      axios(process.env.VUE_APP_API_URL + 'user/total-following/' + localStorage.getItem('user')).then(response => {
        if (response.status !== 200) return;
        this.total_followers = response.data;
      }).catch(() => {});
    },

    createRequest() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'service/create', this.service)
        .then(response => {
          if (response.status === 200) {
            this.service.service = '';
            this.service.description = '';
            this.service.country_id = 160;
            this.service.state_id = '';
            this.service.city_id = '';
            this.service.address = '';
            this.service.end_date = '';
            window.Fire && window.Fire.emit('action');
            this.showCreateModal = false;
            this.loading = false;
          } else {
            this.loading = false;
          }
        })
        .catch(() => { this.loading = false; });
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from = data.from;
      this.pagination.to = data.to;
    },

    formatDate(date) {
      return moment(date).fromNow();
    },

    formatDate2(date) {
      return moment(date).format('MMM DD, YYYY');
    },
  },

  mounted() {
    window.Fire && window.Fire.on('action', () => {
      this.fetchData();
      this.fetchCountries();
      this.fetchNigeriaStates();
      this.fetchLagosCities();
    });
  },
};
</script>

<style scoped>
.page-wrap {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 1.5rem;
  padding: 2.5rem 0;
  align-items: start;
}
.sidebar-card {
  background: #fff;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  position: sticky;
  top: 80px;
}
.main-col { min-width: 0; }

/* Sidebar */
.sb-avatar-wrap { display: flex; flex-direction: column; align-items: center; gap: .75rem; margin-bottom: 1.25rem; }
.sb-avatar { width: 80px; height: 80px; border-radius: 50%; object-fit: cover; border: 3px solid var(--border-light); }
.sb-identity { text-align: center; margin-bottom: 1rem; }
.sb-name { font-weight: 700; font-size: 1rem; color: var(--text); margin: 0 0 .25rem; }
.sb-prof { font-size: .8125rem; color: var(--text-muted); margin: 0; }
.sb-stats { display: flex; gap: .5rem; margin-bottom: 1.25rem; border-top: 1px solid var(--border-light); border-bottom: 1px solid var(--border-light); padding: .75rem 0; }
.sb-stat { flex: 1; display: flex; flex-direction: column; align-items: center; }
.sb-stat-val { font-size: 1.125rem; font-weight: 700; color: var(--text); }
.sb-stat-lbl { font-size: .75rem; color: var(--text-muted); }
.sb-nav { display: flex; flex-direction: column; gap: .25rem; }
.sb-nav-link { display: block; padding: .5rem .75rem; border-radius: var(--radius); font-size: .875rem; font-weight: 500; color: var(--text-muted); text-decoration: none; transition: background .15s, color .15s; }
.sb-nav-link:hover, .sb-nav-link--active { background: var(--surface-3); color: var(--primary); }

/* Page header */
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; flex-wrap: wrap; gap: 1rem; }
.page-title { font-size: 1.375rem; font-weight: 700; color: var(--text); margin: 0 0 .25rem; }
.page-sub { font-size: .875rem; color: var(--text-muted); margin: 0; }

/* Requests grid */
.requests-grid { display: flex; flex-direction: column; gap: .875rem; }
.request-card { text-decoration: none; display: block; }
.request-card-inner {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--surface);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem 1.5rem;
  transition: box-shadow .2s, border-color .2s;
}
.request-card:hover .request-card-inner { box-shadow: var(--shadow); border-color: var(--primary); }
.request-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: var(--radius);
  background: rgba(30,58,95,.07);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
}
.request-info { flex: 1; min-width: 0; }
.request-service { font-size: .9375rem; font-weight: 700; color: var(--text); display: block; margin-bottom: .375rem; }
.request-desc { font-size: .8125rem; color: var(--text-muted); margin: 0 0 .5rem; line-height: 1.5; }
.request-date { display: flex; align-items: center; gap: .375rem; font-size: .75rem; color: var(--text-muted); }
.request-arrow { flex-shrink: 0; color: var(--text-muted); opacity: .5; }

/* Empty state */
.empty-state { display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 4rem 1rem; color: var(--text-muted); text-align: center; }
.empty-state svg { opacity: .35; }
.empty-state p { font-size: 1rem; margin: 0; }

/* Pagination */
.pagination-bar { display: flex; align-items: center; justify-content: center; gap: 1rem; padding: 1.5rem 0; }
.pg-btn { display: flex; align-items: center; gap: .375rem; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius); padding: .5rem 1rem; font-size: .875rem; font-weight: 600; color: var(--text); cursor: pointer; transition: background .15s; }
.pg-btn:disabled { opacity: .4; cursor: not-allowed; }
.pg-btn:not(:disabled):hover { background: var(--surface-2); }
.pg-info { font-size: .875rem; color: var(--text-muted); }

/* Form fields */
.sc-field { display: flex; flex-direction: column; gap: .375rem; margin-bottom: 1rem; }
.sc-field label { font-size: .875rem; font-weight: 600; color: #334155; }
.sc-field input,
.sc-field select,
.sc-field textarea {
  width: 100%;
  padding: .625rem .875rem;
  border: 1.5px solid var(--border);
  border-radius: var(--radius);
  font-family: inherit;
  font-size: .9375rem;
  color: var(--text);
  background: #fff;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  box-sizing: border-box;
}
.sc-field input:focus, .sc-field select:focus, .sc-field textarea:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(30,58,95,.1); }
.req { color: var(--danger); }
.field-group-label { font-size: .875rem; font-weight: 600; color: #334155; margin: 0 0 .75rem; }
.form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0 1rem; }

/* Modal */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 500; display: flex; align-items: center; justify-content: center; padding: 1rem; }
.modal-panel { background: #fff; border-radius: var(--radius-lg); box-shadow: var(--shadow-lg); width: 100%; max-width: 680px; max-height: 90vh; overflow-y: auto; }
.modal-panel--wide { max-width: 740px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--border-light); }
.modal-title { font-size: 1.0625rem; font-weight: 700; color: var(--text); margin: 0; }
.modal-close { background: none; border: none; cursor: pointer; color: var(--text-muted); display: flex; align-items: center; padding: .25rem; }
.modal-body { padding: 1.5rem; }
.modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--border-light); display: flex; justify-content: flex-end; gap: .75rem; }

/* Spinner */
.sc-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .page-wrap { grid-template-columns: 1fr; }
  .sidebar-card { position: static; }
  .form-grid-3 { grid-template-columns: 1fr; }
  .page-header { flex-direction: column; align-items: flex-start; }
  .request-card-inner { flex-wrap: wrap; }
}
:deep(.ql-editor) { min-height: 160px; }
</style>
